import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, priceModeShort } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Star, TrendingUp, Clock, Plus, Package } from "lucide-react";

function Row({ item, onPick }) {
  const mode = priceModeShort(item.pricing_mode);
  const isSurDevis = item.pricing_mode === "sur_devis";
  return (
    <button
      data-testid={`picker-item-${item.id}`}
      onClick={() => onPick(item)}
      className="w-full flex items-center justify-between gap-3 p-3 rounded-md hover:bg-slate-50 text-left border border-slate-100"
    >
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 truncate">
          {item.reference && <span className="text-[#0284C7] mr-1">[{item.reference}]</span>}
          {item.designation}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {[item.brand, item.model].filter(Boolean).join(" · ")}
          {item.category ? `${(item.brand || item.model) ? " — " : ""}${item.category}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-bold text-slate-900">
          {isSurDevis ? <span className="text-amber-600 text-sm">Sur devis</span> : <>{eur(item.sale_price)}<span className="text-xs text-slate-400">/{item.unit}</span></>}
        </div>
        {mode && !isSurDevis && <div className="text-[10px] text-amber-600 font-semibold">{mode}</div>}
        {item.favorite && <Star size={14} className="inline fill-amber-500 text-amber-500" />}
      </div>
    </button>
  );
}

export default function LibraryPicker({ open, onOpenChange, onPick, onCreateBlank, onPickPack }) {
  const [tab, setTab] = useState("prestations");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [sugg, setSugg] = useState({ favorites: [], most_used: [], recent: [] });
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    if (open) {
      setTab("prestations");
      setSearch("");
      setResults([]);
      api.get("/library/suggestions").then((r) => setSugg(r.data)).catch(() => {});
      api.get("/packs").then((r) => setPacks(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (search.trim().length >= 1) {
        api.get("/library", { params: { search, active: true } }).then((r) => setResults(r.data)).catch(() => {});
      } else {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [search, open]);

  const pick = (item) => { onPick(item); onOpenChange(false); };
  const pickPack = (pack) => { onPickPack && onPickPack(pack); onOpenChange(false); };

  const Section = ({ icon: Icon, title, items }) =>
    items.length > 0 ? (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon size={14} /> {title}</div>
        {items.map((it) => <Row key={it.id} item={it} onPick={pick} />)}
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Ajouter au devis</DialogTitle></DialogHeader>

        <div className="flex gap-2">
          <button data-testid="picker-tab-prestations" onClick={() => setTab("prestations")} className={`flex-1 h-10 rounded-md font-semibold text-sm ${tab === "prestations" ? "bg-[#0284C7] text-white" : "bg-slate-100 text-slate-700"}`}>Prestations</button>
          {onPickPack && (
            <button data-testid="picker-tab-packs" onClick={() => setTab("packs")} className={`flex-1 h-10 rounded-md font-semibold text-sm ${tab === "packs" ? "bg-[#0284C7] text-white" : "bg-slate-100 text-slate-700"}`}>Packs {packs.length > 0 ? `(${packs.length})` : ""}</button>
          )}
        </div>

        {tab === "prestations" || !onPickPack ? (
          <>
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                data-testid="picker-search-input"
                placeholder="Rechercher (réf. CL01, Bosch, mise en service…)"
                className="h-12 pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="space-y-4">
              {search.trim() ? (
                results.length > 0 ? (
                  <div className="space-y-2">{results.map((it) => <Row key={it.id} item={it} onPick={pick} />)}</div>
                ) : (
                  <p className="text-slate-500 text-sm py-4 text-center">Aucun résultat. Créez une nouvelle ligne ci-dessous.</p>
                )
              ) : (
                <>
                  <Section icon={Star} title="Mes favoris" items={sugg.favorites} />
                  <Section icon={TrendingUp} title="Les plus utilisés" items={sugg.most_used} />
                  <Section icon={Clock} title="Dernières lignes utilisées" items={sugg.recent} />
                  {sugg.favorites.length === 0 && sugg.most_used.length === 0 && sugg.recent.length === 0 && (
                    <p className="text-slate-500 text-sm py-2 text-center">Votre bibliothèque se remplira au fil de vos devis.</p>
                  )}
                </>
              )}
            </div>

            <Button
              data-testid="picker-create-blank"
              variant="outline"
              onClick={() => { onCreateBlank(); onOpenChange(false); }}
              className="h-12 w-full border-dashed border-[#0284C7] text-[#0284C7] font-bold"
            >
              <Plus size={18} className="mr-1" /> Créer une nouvelle ligne
            </Button>
          </>
        ) : (
          <div className="space-y-2">
            {packs.length === 0 && <p className="text-slate-500 text-sm py-4 text-center">Aucun pack. Créez-en dans la Bibliothèque &gt; Packs.</p>}
            {packs.map((p) => (
              <button
                key={p.id}
                data-testid={`picker-pack-${p.id}`}
                onClick={() => pickPack(p)}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-md hover:bg-slate-50 text-left border border-slate-100"
              >
                <div className="min-w-0 flex items-center gap-2">
                  <Package size={18} className="text-[#0284C7] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.category || "—"} · {(p.items || []).length} ligne(s)</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
